import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import ProfileFieldset, { buildProfilePayload } from "@/components/interest/ProfileFieldset";
import { NeedsLoginPanel, DuplicatePrompt, SuccessScreen } from "@/components/interest/InterestShared";
import { MapPin, CalendarDays, Trophy, ShieldCheck } from "lucide-react";

const inputCls = "w-full px-4 py-3 border border-slate-300 rounded-sm text-sm focus:outline-none focus:border-[#0A192F]";
const PREFERRED_ROLES = ["Primary position", "Secondary position", "Pitcher only", "Position player only", "Two-way"];
const PASSPORT = ["Valid", "Expires before event", "In progress", "None"];

function YesNo({ label, value, onChange, testid, required }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
        {label}{required && <span className="text-[#E63946]"> *</span>}
      </label>
      <select data-testid={testid} className={`${inputCls} bg-white mt-1.5`}
        value={value === true ? "yes" : value === false ? "no" : ""}
        onChange={(e) => onChange(e.target.value === "" ? null : e.target.value === "yes")}>
        <option value="">Select…</option>
        <option value="yes">Yes</option>
        <option value="no">No</option>
      </select>
    </div>
  );
}

export default function DominicanInterest() {
  const { slug = "dominican-republic" } = useParams();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [f, setF] = useState({});
  const [dr, setDr] = useState({});
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [needsLogin, setNeedsLogin] = useState(null);
  const [dup, setDup] = useState(null);
  const [success, setSuccess] = useState(null);

  const set = useCallback((k, v) => setF((p) => ({ ...p, [k]: v })), []);
  const setW = useCallback((k, v) => setDr((p) => ({ ...p, [k]: v })), []);

  useEffect(() => {
    api.get(`/interest-events/${slug}`).then(({ data }) => setEvent(data)).catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    if (!user) return;
    api.get("/players/me").then(({ data }) => {
      const p = data?.player || data;
      if (!p) return;
      setF((prev) => ({
        ...prev,
        first_name: p.first_name || "", last_name: p.last_name || "", email: p.email || user.email || "",
        phone: p.phone || "", date_of_birth: p.date_of_birth || "", graduation_year: p.graduation_year || "",
        city: p.city || "", province: p.province || "", primary_position: p.primary_position || "",
        secondary_position: p.secondary_position || "", bats: p.bats || "", throws: p.throws || "",
        current_team: p.current_team || "", league: p.league || "", school: p.school || "",
        parent_name: p.parent_name || "", parent_email: p.parent_email || "", parent_phone: p.parent_phone || "",
        instagram_handle: p.instagram_handle || "", x_handle: p.x_handle || "",
        profile_photo_url: p.profile_photo_url || "",
      }));
    }).catch(() => {});
  }, [user]);

  const submit = async (extra = {}) => {
    setError("");
    const required = { first_name: "First name", last_name: "Last name", email: "Player email",
      date_of_birth: "Date of birth", graduation_year: "Graduation year", province: "Province",
      primary_position: "Primary position", school: "High school" };
    const missing = Object.entries(required).filter(([k]) => !String(f[k] || "").trim()).map(([, l]) => l);
    if (missing.length) { setError(`Please complete: ${missing.join(", ")}`); window.scrollTo({ top: 0, behavior: "smooth" }); return; }
    if (!consent) { setError("Please acknowledge this is an expression of interest only."); return; }
    setBusy(true);
    try {
      const { profile, media } = buildProfilePayload(f);
      const { data } = await api.post("/interests/submit", {
        event_slug: slug, profile, media, wwba: dr, consent: true, source: "dominican_interest_form", ...extra,
      });
      if (data.needs_login) { setNeedsLogin(data.message); setDup(null); return; }
      if (data.possible_duplicate) { setDup(data); return; }
      if (data.ok) {
        setSuccess({ claimRequired: data.claim_required, playerSlug: null,
          message: data.confirmation_message || event?.confirmation_message ||
            "Thank you for registering your interest. Our staff will review and follow up." });
      }
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (notFound) {
    return <PublicLayout><div className="max-w-3xl mx-auto p-16 text-center font-display text-2xl uppercase text-slate-500" data-testid="event-not-found">Event not found</div></PublicLayout>;
  }
  if (!event) {
    return <PublicLayout><div className="max-w-3xl mx-auto p-10 text-xs uppercase tracking-wider text-slate-500">Loading…</div></PublicLayout>;
  }

  const closed = event.application_status === "closed" || event.interest_open === false;
  const divisions = event.age_divisions || [];

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="relative bg-[#0A192F] text-white border-b-4 border-[#E63946] overflow-hidden">
        {event.hero_image && (
          <img src={event.hero_image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        )}
        <div className={`relative max-w-4xl mx-auto px-6 py-14 ${event.logo_image ? "sm:pr-56" : ""}`}>
          {event.logo_image && <img src={event.logo_image} alt="" data-testid="interest-event-logo" className="hidden sm:block absolute right-6 top-1/2 -translate-y-1/2 h-40 w-44 object-contain drop-shadow-xl" />}
          <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[#E63946] bg-white/5 px-3 py-1.5 rounded-sm">
            <Trophy className="w-3.5 h-3.5" /> {event.event_type || "International Showcase"} · Expression of Interest
          </div>
          <h1 data-testid="dr-title" className="font-display text-4xl sm:text-5xl uppercase tracking-tighter mt-4">{event.name}</h1>
          {event.description && <p className="text-white/70 text-sm mt-3 max-w-2xl leading-relaxed">{event.description}</p>}
          <div className="flex flex-wrap gap-x-6 gap-y-2 mt-5 text-sm text-white/80">
            {event.location && <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4 text-[#E63946]" /> {event.location}</span>}
            {(event.start_date || event.end_date) && <span className="inline-flex items-center gap-1.5"><CalendarDays className="w-4 h-4 text-[#E63946]" /> {[event.start_date, event.end_date].filter(Boolean).join(" – ")}</span>}
            {event.application_deadline && <span className="inline-flex items-center gap-1.5">Apply by {event.application_deadline}</span>}
          </div>
          {(divisions.length > 0 || (event.eligible_grad_years || []).length > 0 || event.eligibility) && (
            <div className="mt-4 text-xs text-white/60">
              {divisions.length > 0 && <span className="mr-4">Divisions: {divisions.join(", ")}</span>}
              {(event.eligible_grad_years || []).length > 0 && <span className="mr-4">Grad years: {(event.eligible_grad_years).join(", ")}</span>}
              {event.eligibility && <div className="mt-1">{event.eligibility}</div>}
            </div>
          )}
        </div>
      </section>

      <section className="bg-[#F1F3F6] py-12">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-white border border-slate-200 rounded-sm p-6 sm:p-10">
            {success ? (
              <SuccessScreen title="Interest Registered!" message={success.message} claimRequired={success.claimRequired} playerSlug={success.playerSlug} />
            ) : needsLogin ? (
              <NeedsLoginPanel message={needsLogin} redirectTo={`/interest/${slug}`} />
            ) : dup ? (
              <DuplicatePrompt candidates={dup.candidates} message={dup.message} busy={busy}
                onPickExisting={(id) => submit({ confirm_existing_player_id: id })}
                onCreateNew={() => submit({ acknowledge_new: true })} />
            ) : closed ? (
              <div className="text-center py-10" data-testid="interest-closed">
                <ShieldCheck className="w-12 h-12 text-slate-300 mx-auto" />
                <div className="font-display text-2xl uppercase text-[#0A192F] mt-4">Interest Closed</div>
                <p className="text-sm text-slate-600 mt-2">Submissions for this event are currently closed. Please check back soon.</p>
              </div>
            ) : (
              <>
                {error && <div data-testid="dr-error" className="border-l-4 border-[#E63946] bg-red-50 p-3 text-sm text-[#E63946] mb-6">{error}</div>}

                <ProfileFieldset f={f} set={set} testidPrefix="dr" />

                {/* Dominican Republic-specific */}
                <section className="mt-8 pt-8 border-t border-slate-200">
                  <div className="text-xs font-semibold uppercase tracking-wider text-[#E63946] mb-4">Dominican Republic — Interest Details</div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Passport Status</label>
                      <select data-testid="dr-passport" className={`${inputCls} bg-white mt-1.5`} value={dr.passport_status || ""} onChange={(e) => setW("passport_status", e.target.value)}>
                        <option value="">Select…</option>
                        {PASSPORT.map((p) => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Interested Division</label>
                      {divisions.length > 0 ? (
                        <select data-testid="dr-division" className={`${inputCls} bg-white mt-1.5`} value={dr.division || ""} onChange={(e) => setW("division", e.target.value)}>
                          <option value="">Select…</option>
                          {divisions.map((d) => <option key={d} value={d}>{d}</option>)}
                        </select>
                      ) : (
                        <input data-testid="dr-division" className={`${inputCls} mt-1.5`} value={dr.division || ""} onChange={(e) => setW("division", e.target.value)} />
                      )}
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Preferred Role</label>
                      <select data-testid="dr-role" className={`${inputCls} bg-white mt-1.5`} value={dr.preferred_role || ""} onChange={(e) => setW("preferred_role", e.target.value)}>
                        <option value="">Select…</option>
                        {PREFERRED_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </div>
                    <YesNo label="Previous CBR Nation player?" value={dr.previous_cbr_nation} onChange={(v) => setW("previous_cbr_nation", v)} testid="dr-prev-cbr" />
                    <YesNo label="Previous international showcase experience?" value={dr.previous_international} onChange={(v) => setW("previous_international", v)} testid="dr-prev-intl" />
                    <YesNo label="Available for full event dates?" value={dr.available_full_tournament} onChange={(v) => setW("available_full_tournament", v)} testid="dr-available" />
                    <YesNo label="Able to travel to the Dominican Republic?" value={dr.can_travel_dominican} onChange={(v) => setW("can_travel_dominican", v)} testid="dr-travel" />
                    <YesNo label="Parent/guardian travelling with player?" value={dr.parent_travelling} onChange={(v) => setW("parent_travelling", v)} testid="dr-parent-travel" />
                  </div>
                  <div className="mt-4">
                    <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Why do you want to represent CBR Nation?</label>
                    <textarea data-testid="dr-why" rows={3} className={`${inputCls} mt-1.5`} value={dr.why_represent || ""} onChange={(e) => setW("why_represent", e.target.value)} />
                  </div>
                </section>

                {/* Private info */}
                <section className="mt-8 pt-8 border-t border-slate-200">
                  <div className="text-xs font-semibold uppercase tracking-wider text-[#E63946] mb-1">Private — Admins Only</div>
                  <p className="text-xs text-slate-500 mb-4">Emergency and travel details below are kept private and are never shown publicly.</p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Emergency Contact Name</label>
                      <input data-testid="dr-emergency-name" className={`${inputCls} mt-1.5`} value={dr.emergency_contact_name || ""} onChange={(e) => setW("emergency_contact_name", e.target.value)} />
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Emergency Contact Phone</label>
                      <input data-testid="dr-emergency-phone" className={`${inputCls} mt-1.5`} value={dr.emergency_contact_phone || ""} onChange={(e) => setW("emergency_contact_phone", e.target.value)} />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Scheduling, medical, or travel notes for CBR</label>
                      <textarea data-testid="dr-notes" rows={3} className={`${inputCls} mt-1.5`} value={dr.notes || ""} onChange={(e) => setW("notes", e.target.value)} />
                    </div>
                  </div>
                </section>

                <label className="mt-8 flex items-start gap-3 cursor-pointer">
                  <input data-testid="dr-consent" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1" />
                  <span className="text-sm text-slate-700">
                    I understand this is an <strong>expression of interest only</strong> and does not confirm a roster spot or registration. I consent to CBR Prospects contacting me about this opportunity.
                  </span>
                </label>

                <button data-testid="dr-submit" onClick={() => submit()} disabled={busy}
                  className="mt-8 w-full bg-[#E63946] hover:bg-[#D90429] text-white py-4 text-sm font-semibold uppercase tracking-wider rounded-sm disabled:opacity-60 transition-colors">
                  {busy ? "Submitting…" : "Register Your Interest"}
                </button>
              </>
            )}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
